import {
  IBidResponse,
  ICrewsNonFlyingAssignment,
  IDisplayBidNFA,
  IDisplayBidPairing,
  IDisplayPairing,
  INonFlyingAssignment,
  IPairingsLogicModifierGL,
  IScrollClickResponse,
  isIPairingPosition,
  IViolationResult,
  minuteInMilliseconds,
  ScrollBarClick,
} from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { crewBidRowHeight, getSelectedCrewBidRowHeight } from "src/components/CrewSchedule/CrewMemberBidding";
import {
  IBaseTime,
  ICrewBidNonFlyingAssignmentMap,
  ICrewBidPairingMap,
  ICrewEvent,
  ICrewLogicModifierMap,
  ICrewMember,
  ICrewNonFlyingAssignmentMap,
  ICrewPairing,
  ICrewPairingMap,
  ICrewPairingPosition,
  ICrewQualifications,
  ICrewSchedEventMap,
  ICrewScheduleBaseEvent,
  ICrewScheduleEvent,
  ICrewScheduleEventMap,
  ICrewViolationsMap,
  IDayLogicModifierMap,
  IQualificationMap,
  ISelectedCrewDay,
  ISelectedCrewDays,
  isIDisplayPairing,
  ITemporaryPairing,
} from "src/redux/reducers/CrewScheduleReducer";
import { timeScales } from "src/SharedGL/TimelineUtils";
import { BaseTimelineGL } from "../../../SharedGL/BaseTimelineGL";
import { Camera } from "../../../SharedGL/Camera";
import { IOutlineBox, OutlinesGL } from "../../../SharedGL/OutLines/OutlinesGL";
import { ScrollBarWrapper } from "../../../SharedGL/ScrollBar/ScrollBarWrapper";
import { loadTexture } from "../../../SharedGL/Shader";
import { IRGBA } from "../../FlightTimeline/TimelineUtils";
import { ICrewMemberGLDimensions } from "../CrewScheduleCanvas";
import { getNFAsInRangeByRange, getPairingsInRangeByRange } from "../TimelineUtils";
import { drawBidAssignmentIndicators } from "./bidAssignmentIndicatorsGL";
import { drawCheckInOutIndicators } from "./checkInOutIndicatorsGL";
import { CrewOverLapsGL } from "./CrewOverLapsGL";
import { CrewQualificationsGL } from "./CrewQualificationsGL";
import { CrewScheduleErrorsGL } from "./CrewScheduleErrorsGL";
import { CrewViolationsGL } from "./CrewViolationsGL";
import { HighlightedPairingGL } from "./HighlightedPairing/HighlightedPairingGL";
import { NonFlyingAssignmentsGL } from "./NonFlyingAssignmentsGL";
import { PairingsGL } from "./PairingsGL";
import { PlannedEventsGL } from "./PlannedEventsGL";
import { PairingShader } from "./shaders/PairingShader";
import { TextureShader } from "./shaders/TextureShader";
import { ViolationBarShader } from "./shaders/ViolationBarShader";
import { ViolationBar } from "./ViolationBar";
import { IAirportOptionMap } from "src/redux/reducers/StructuresReducer";
import { CrewMember } from "src/components/CrewSchedule/PairingEditorWindow/CrewMember";
import { makeDayMode, PerDayViolationOverlayGL } from "./PerDayViolationOverlayGL";
import { DayMode } from "./PerDayViolationOverlayGL";
export interface ICrewGL {
  rowStart: number;
  rowHeight: number;
  isSelected: boolean;
  isExpanded: boolean;
  violationBar: ViolationBar;
  plannedEventsGL?: PlannedEventsGL;
  pairingsGL?: PairingsGL;
  violationsGL?: CrewViolationsGL;
  qualificationsGL?: CrewQualificationsGL;
  dayLogicModifiers?: IDayLogicModifierMap;
  nonFlyingAssignmentsGL?: NonFlyingAssignmentsGL;
  overLapsGL?: CrewOverLapsGL;
}

export interface ICrewGLMap {
  [crewId: string]: ICrewGL;
}

const oneDayMillis = 8.64e7;

/** Ghost pairing (hovered pairings) conflict types - add as required */
export enum GhostPairingConflicts {
  ALREADY_ASSIGNED = "This pairing is already assigned to this crew member.",
  PAIRING_OVERLAP = "This pairing overlaps another already assigned to this crew member.",
  POSITION_FULFILLED = "Desired position assignment already fulfilled on this pairing.",
  NFA_OVERLAP = "This pairing overlaps non-flying events assigned to this crew member.",
  NO_POSITION_MATCH = "This pairing has no positions that match this crew member.",
  NON_PRIMARY_ASSIGNMENT = "This pairing's position is not listed as the crew member's primary position",
}

const white = { red: 1, green: 1, blue: 1, alpha: 1.0 };
const red = { red: 1, green: 0, blue: 0, alpha: 1 };
const orange = { red: 1, green: 0.6, blue: 0, alpha: 1 };

interface IGhostDetails {
  color: IRGBA;
  error: GhostPairingConflicts;
}

/**
 * This function checks a selected dragged pairing (referred to as ghost pairing) vs that crew members currently loaded pairings, nfa for any conflicts.
 * @param crewPairings - All pairings currently assigned to this crew member (only checks those loaded into redux)
 * @param crewNFAs - All NFA currently assigned to this crew member (only checks those loaded into redux)
 * @param ghostPairing - pairing we wish to have assigned to this crew member (includes pos)
 * @returns - object containing color for the ghosted pairing as well as conflict message.
 */
const getGhostDetails = (
  crewPairings: IDisplayPairing[],
  crewNFAs: INonFlyingAssignment[],
  ghostPairing: ITemporaryPairing,
  crewMember: ICrewMember
): IGhostDetails => {
  const alreadyAssigned = crewPairings.find((p) => ghostPairing.pairingId === p._id);
  const overlappingPairings = crewPairings.filter((p) => {
    return p.start?.valueOf() <= ghostPairing.end && ghostPairing.start <= p.end?.valueOf(); // start/end times overlaps
  });
  const overlappingNFAs = crewNFAs.filter((nfa) => {
    return nfa.start?.valueOf() <= ghostPairing.end && ghostPairing.start <= nfa.end?.valueOf(); // start/end times overlaps
  });
  const filledPositionConflict = ghostPairing?.positions?.find(
    (p) => p.position === ghostPairing?.assignPos && p.hasOwnProperty("crew_id")
  );
  const noPosMatchConflict = !ghostPairing?.positions?.find((p) => p.position === ghostPairing?.assignPos);
  const primaryPositionConflict = ghostPairing?.positions?.find(
    (p) =>
      p.position === ghostPairing?.assignPos &&
      p.hasOwnProperty("crew_id") &&
      crewMember.crewInfo.positions[0].title === p.position
  );
  // Return ghost color and error if conflicts are found with pairing hovering over crew timeline.
  switch (true) {
    case !!noPosMatchConflict:
      return { color: red, error: GhostPairingConflicts.NO_POSITION_MATCH };
    case !!alreadyAssigned:
      return { color: red, error: GhostPairingConflicts.ALREADY_ASSIGNED };
    case !!overlappingPairings?.length:
      return { color: red, error: GhostPairingConflicts.PAIRING_OVERLAP };
    case !!overlappingNFAs?.length:
      return { color: red, error: GhostPairingConflicts.NFA_OVERLAP };
    case !!filledPositionConflict:
      return { color: orange, error: GhostPairingConflicts.POSITION_FULFILLED };
    case !!primaryPositionConflict:
      return { color: orange, error: GhostPairingConflicts.NON_PRIMARY_ASSIGNMENT };
    default:
      return { color: white, error: null }; // no conflicts found
  }
};

export interface IHoveredEvent {
  event: IDisplayPairing | IDisplayBidPairing | ICrewsNonFlyingAssignment | IDisplayBidNFA;
  crewId: string;
  isPlanned: boolean;
}

export interface IOpenFlyinPairingRequest {
  pairing: IDisplayPairing;
  requestors: string[];
}

export const baseRowHeight = 50;
export const expandedRowHeight = 80;

export class CrewScheduleGL extends BaseTimelineGL {
  public crewList: ICrewPairingPosition[];
  private crewGLMap: ICrewGLMap;

  private pairingShader: PairingShader;
  private textureShader: TextureShader;
  private violationBarShader: ViolationBarShader;
  private hoveredEvents: ICrewEvent[];
  private outlinedEvents: ICrewEvent[]; // renamed from selectedEvents because some selected events might not be visible
  private focusedEvents: ICrewEvent[];
  private selectedCrewDays: ISelectedCrewDays = { crewId: "", selectedTimes: [], timezone: "Etc/UTC" };
  private outlinesGL: OutlinesGL;
  private selectedOutlinesGL: OutlinesGL;
  private selectedDayOutlinesGL: OutlinesGL;
  private focusedOutlinesGL: OutlinesGL;
  private perDayOverlayGL: PerDayViolationOverlayGL;

  private lastModifiedTime: number;
  private dayScale: number;
  private numDaysInView: number;

  private nonFlyingAssignmentTexture: WebGLTexture;
  private violationDayTexture: WebGLTexture;
  private scrollBarWrapper: ScrollBarWrapper;
  private scrollBarThumbColor: IRGBA;
  private crewNFAs: ICrewNonFlyingAssignmentMap;
  private crewPairings: ICrewPairingMap;
  private unassignedCrewPairings: ICrewPairingMap;
  private crewEvents: ICrewScheduleEventMap;

  private hideDropOffHours: boolean;
  private hideTimeBetweenEvents: boolean;
  private hideCheckInOutTimes: boolean;

  private focusedEventOutlineColor: IRGBA;

  public ghostPairing: ITemporaryPairing | null;
  public ghostPairingConflict: GhostPairingConflicts | null;

  private hoveredPairingGL: HighlightedPairingGL;

  // New variables used to store bidding related information needed in the webgl component.
  public bidAssignmentMode: boolean;
  public biddingBlockTimes: number[] | null;
  public bidRankSettings: [{ prefRank: number; label: string; color: string }] | null;
  private crewBidPairings: ICrewBidPairingMap;
  private crewBidNFAs: ICrewBidNonFlyingAssignmentMap;

  constructor(mount: HTMLElement, width: number, height: number) {
    super(mount, width, height);
    this.crewList = [];
    this.crewGLMap = {};
    this.lastModifiedTime = 0;
    this.scrollBarWrapper = new ScrollBarWrapper(this.gl);
    this.scrollBarThumbColor = this.theme.scrollBarThumb;
    this.hoveredEvents = [];
    this.outlinedEvents = [];
    this.focusedEvents = [];
    this.outlinesGL = new OutlinesGL(this.gl);
    this.selectedOutlinesGL = new OutlinesGL(this.gl);
    this.selectedDayOutlinesGL = new OutlinesGL(this.gl);
    this.focusedOutlinesGL = new OutlinesGL(this.gl);
    this.nonFlyingAssignmentTexture = loadTexture(this.gl, "/assets/timeline-icons/diagonal-pattern-256.png");
    this.violationDayTexture = loadTexture(this.gl, "/assets/timeline-icons/violationDay.png");
    this.updateDayScale();

    this.pairingShader = new PairingShader(this.gl);
    this.textureShader = new TextureShader(this.gl);
    this.violationBarShader = new ViolationBarShader(this.gl);

    this.perDayOverlayGL = new PerDayViolationOverlayGL(
      this.gl,
      this.textureShader,
      this.violationDayTexture,
      this.pairingShader
    );

    this.crewNFAs = {};
    this.crewPairings = {};
    this.unassignedCrewPairings = {};
    this.crewEvents = {};
    this.hideDropOffHours = true;
    this.hideTimeBetweenEvents = true;
    this.hideCheckInOutTimes = true;

    this.focusedEventOutlineColor = this.theme.selectedFlightColor;
    this.ghostPairing = null;
    // Since we need to determine conflicts on hover (to display ghost in red or white)
    // Store the type of conflict here so that we don't have to recalculate it on drop.
    this.ghostPairingConflict = null;

    this.hoveredPairingGL = new HighlightedPairingGL(this.gl, white);

    // Bid assignment variables.
    this.bidAssignmentMode = false;
    this.biddingBlockTimes = null;
    this.bidRankSettings = null;
    this.crewBidPairings = {};
    this.crewBidNFAs = {};
  }

  /** Allow parent component to set bid assignment mode true/false */
  public setBidAssignmentMode = (state: boolean): void => {
    this.bidAssignmentMode = state;
  };

  /** Allow parent component to set bidding block start end period */
  // Add buffer to time axis so planners can see edges of bidding block.
  public setBiddingBlockTimes = (range: number[] | null): void => {
    const setTimes = Array.isArray(range) && range?.length === 2;
    this.biddingBlockTimes = setTimes ? range : null;
    if (setTimes) {
      // If the currently assigned block time exceeds a week, only set the crew sched zoom to a max of a week initially (users can scroll for the rest)
      const blockLength = range[1] - range[0];
      const endTime = blockLength > timeScales.week ? range[0] + timeScales.week : range[1] + timeScales.day;
      this.setTimeAxis(range[0] - timeScales.day, endTime);
    }
  };

  /** Store bid rank settings including count and colors of bid ranks */
  public setBidRankSettings = (settings: [{ prefRank: number; label: string; color: string }] | null): void => {
    this.bidRankSettings = settings;
  };

  /**
   * setTempPairingOverlay draws a ghosted view of a potential pairing using it's start and end time.
   * ghosted pairing requires a crew member id in order to draw on that specific row.
   * @param ghostPairing - temporary pairing object
   * @param YPos - used to determine crewmember user is hovering over
   */
  public setTempPairingOverlay = (
    pairingDrop: Partial<IDisplayPairing> | null,
    assignPos: string,
    YPos?: number
  ): void => {
    const crewId = YPos ? this.getCrewMemberFromYPixel(YPos) : null;
    const newGhostPairing: ITemporaryPairing = {
      start: pairingDrop?.start?.valueOf(),
      end: pairingDrop?.end?.valueOf(),
      crewId: crewId,
      pairingId: pairingDrop?._id,
      shortId: pairingDrop?.shortId,
      positions: pairingDrop?.positions,
      assignPos: assignPos ?? "",
    };
    // If no change to pairingId or crewId, do nothing.
    if (
      this.ghostPairing?.pairingId === newGhostPairing?.pairingId &&
      this.ghostPairing?.crewId === newGhostPairing?.crewId
    ) {
      return;
    }

    this.ghostPairing = pairingDrop?._id ? newGhostPairing : null;
    this.draw(); // Initiate a draw call;
  };

  /**
   * function which draws a ghosted pairing block indicate start/end of pairing desired for assignment on a particular crew member.
   * @param crewGL - crewGl context
   * @param crewPairings - currently assigned crew pairings used to check for overlaps.
   * @param ghostPairing - info required to draw ghosted pairing block.
   */
  private drawGhostPairing(
    crewGL: ICrewGL,
    crewPairings: IDisplayPairing[],
    crewNFAs: INonFlyingAssignment[],
    ghostPairing: ITemporaryPairing,
    crewMember: ICrewMember
  ): void {
    const { color, error } = getGhostDetails(crewPairings, crewNFAs, ghostPairing, crewMember);
    const colors = {
      outline: color,
      fill: { ...color, alpha: 0.25 },
    };
    this.ghostPairingConflict = error;
    const pairingHeight = 25;
    this.hoveredPairingGL.drawHighlightedPairing(
      this.gl,
      this.camera,
      [ghostPairing.start, ghostPairing.end],
      [crewGL.rowStart + PairingsGL.pairingYStart, pairingHeight],
      colors
    );
  }

  public setBaseTime(baseTime: IBaseTime): void {
    super.setBaseTime(baseTime);
    //this.setSelectedCrewDay(this.selectedCrewDay);
    this.setSelectedCrewDays(this.selectedCrewDays);
  }

  public setAirportInfo(isICAO: boolean, airportOptions: IAirportOptionMap): void {
    this.isICAO = isICAO;
    this.airportOptions = airportOptions;
    this.timelineLabelsGL.setAirportInfo(isICAO, airportOptions);
  }

  /**
   * getCrewMemberFromYPixel -
   * @param yPixel - the yPixel to find the crew member
   */
  public getCrewMemberFromYPixel(yPixel: number): any {
    const [yMin] = this.camera.getYRange();
    //! For bidding mode, selected nfa row height needs to be used here along with timelineStart in order to retrieve current crewMember.
    const y = yPixel + yMin - (BaseTimelineGL.timelineStart + this.returnNFARowHeight());
    for (let i = this.minRowIndex; i <= this.maxRowIndex; i++) {
      const crewMember = this.crewList[i];
      const crewId = isIPairingPosition(crewMember) ? `${crewMember.position}-${i}` : crewMember._id;
      const crewGL = this.crewGLMap[crewId];
      if (y >= crewGL.rowStart && y <= crewGL.rowStart + crewGL.rowHeight) {
        return crewId;
      }
    }
    return null;
  }

  /**
   * getTimeRangeYPixelFromCrewMember -
   * @param crewId - the crewId needed to return Ypos.
   */
  public getYPixelFromCrewMember(crewId: string): number {
    //const [yMin] = this.camera.getYRange();
    const crewGL = this.crewGLMap[crewId];
    if (crewGL) {
      return crewGL.rowStart + crewGL.rowHeight / 2;
    }
    return null;
  }

  public getCrewMemberDimensions(crewId: string): ICrewMemberGLDimensions | null {
    const crewGL = this.crewGLMap[crewId];
    if (!crewGL) {
      return null;
    }
    const { rowStart, rowHeight, isExpanded } = crewGL;
    return {
      rowStart,
      rowHeight,
      isExpanded,
    };
  }

  public getHoveredCrewMember(x: number, yPixel: number): any {
    return this.getCrewMemberFromYPixel(yPixel);
  }

  /**
   * set the selected events outline color, true = default, false = same as hover
   */
  public setFocusedEventsOutlineColor = (defaultColor: boolean): void => {
    this.focusedEventOutlineColor = defaultColor ? this.theme.selectedFlightColor : this.theme.hoverFlightColor;
  };

  /**
   * getSelectedEventsOutlineColor returns true if the deafult color is the current color
   */
  public getFocusedEventsOutlineColor = (): boolean => {
    return this.focusedEventOutlineColor === this.theme.selectedFlightColor;
  };

  public setHoveredTime = (time: number): number => {
    return (this.hoveredTime = time);
  };

  public recalculateScrollBar = (): void => {
    this.scrollBarWrapper.calculateScrollBar(this.camera, this.canvas, this.maxHeight); //sets the pixels for size of bar
  };

  public updateCrew(crew: ICrewPairingPosition[], disableExpandedRow?: boolean): void {
    this.crewList = crew;
    let rowStart = 0;
    let index = 0;
    let rowHeight = baseRowHeight;
    let rowSizeChanged = false;
    for (const crewMember of crew) {
      if (isIPairingPosition(crewMember)) {
        const id = `${crewMember.position}-${index}`;
        const violationBar = this.crewGLMap[id]
          ? this.crewGLMap[id].violationBar
          : new ViolationBar(this.gl, this.violationBarShader);
        this.crewGLMap[id] = {
          ...crewMember,
          violationBar,
          rowStart,
          isSelected: false,
          isExpanded: false,
          rowHeight: rowHeight,
        };
      } else {
        // 120px high assuming 3 pref ranks exist.
        const bidAssignmentHeight = this.bidRankSettings?.length
          ? getSelectedCrewBidRowHeight(this.bidRankSettings?.length)
          : baseRowHeight;

        // New parameter disableExpandedRow disables expanded row appearance.
        rowHeight =
          !disableExpandedRow && crewMember.isExpanded && !this.bidAssignmentMode
            ? expandedRowHeight
            : !disableExpandedRow && crewMember.isExpanded && this.bidAssignmentMode
              ? bidAssignmentHeight
              : baseRowHeight;

        if (!this.crewGLMap[crewMember._id]) {
          const pairingsGL = new PairingsGL(this.gl, this.pairingShader);
          const nonFlyingAssignmentsGL = new NonFlyingAssignmentsGL(
            this.gl,
            this.textureShader,
            this.pairingShader,
            this.nonFlyingAssignmentTexture
          );
          const violationBar = new ViolationBar(this.gl, this.violationBarShader);
          const violationsGL = new CrewViolationsGL(
            this.gl,
            this.pairingShader,
            this.textureShader,
            this.violationDayTexture
          );
          const qualificationsGL = new CrewQualificationsGL(
            this.gl,
            this.pairingShader,
            this.textureShader,
            this.violationDayTexture
          );
          const overLapsGL = new CrewOverLapsGL(
            this.gl,
            this.pairingShader,
            this.textureShader,
            this.violationDayTexture
          );
          const plannedEventsGL = new PlannedEventsGL(this.gl, this.pairingShader);
          this.crewGLMap[crewMember._id] = {
            ...crewMember,
            rowStart,
            rowHeight,
            violationBar,
            violationsGL,
            qualificationsGL,
            pairingsGL,
            nonFlyingAssignmentsGL,
            plannedEventsGL,
            overLapsGL,
          };
        }
        this.crewGLMap[crewMember._id].isSelected = crewMember.isSelected;
        rowSizeChanged = rowSizeChanged || crewMember.isExpanded !== this.crewGLMap[crewMember._id].isExpanded;
        this.crewGLMap[crewMember._id].isExpanded = crewMember.isExpanded;
        this.crewGLMap[crewMember._id].rowStart = rowStart;
        this.crewGLMap[crewMember._id].rowHeight = rowHeight;
      }
      index++;
      rowStart += rowHeight;
    }
    this.maxHeight = rowStart;

    if (rowSizeChanged) {
      this.setOutlinedEvents(this.outlinedEvents);
      this.setSelectedCrewDays(this.selectedCrewDays);
    }
    this.updateMinMaxRows();
    this.recalculateScrollBar();
    this.updateCameraRange();
  }

  public updateTemporaryPairings(temporaryPairings: ITemporaryPairing[]): void {
    const foundCrew: any = {};
    for (const temporaryPairing of temporaryPairings) {
      if (foundCrew[temporaryPairing.crewId]) {
        foundCrew[temporaryPairing.crewId].push(temporaryPairing);
      } else {
        foundCrew[temporaryPairing.crewId] = [temporaryPairing];
      }
    }

    //first every crews' temp buffer...
    this.crewList.forEach((crewMember: ICrewPairingPosition, index: number) => {
      const crewId = isIPairingPosition(crewMember) ? `${crewMember.position}-${index}` : crewMember._id;
      // Object.keys(this.crewList).forEach((crewId: string) => {
      const crewGL = this.crewGLMap[crewId];
      if (!crewGL || !crewGL.pairingsGL) {
        return;
      }
      const crewTempPairings = foundCrew[crewId] ?? [];
      crewGL.pairingsGL.updateTempPairingsBuffer(this.gl, crewTempPairings);
    });
  }

  //snap to spot clicked
  public scrollClick(xPos: number, yPos: number): ScrollBarClick {
    const response: IScrollClickResponse = this.scrollBarWrapper.clickedOnScrollBar(
      this.gl,
      this.camera,
      this.canvas,
      xPos,
      yPos,
      this.maxHeight
    );
    if (response.typeOfScroll === 5) {
      this.scrollBarThumbColor = this.theme.scrollBarThumbClickedOn;
    } else {
      this.scrollBarThumbColor = this.theme.scrollBarThumb;
    }
    this.moveCameraByYPixels(response.pixelsToMove);
    return response.typeOfScroll;
  }

  public hoveredOnScrollBarThumb(xPos: number, yPos: number): void {
    const response: boolean = this.scrollBarWrapper.hoveredOnScrollBarThumb(
      this.camera,
      this.canvas,
      xPos,
      yPos,
      this.maxHeight
    );
    if (response) {
      this.scrollBarThumbColor = this.theme.scrollBarThumbHover;
    } else {
      this.scrollBarThumbColor = this.theme.scrollBarThumb;
    }
  }

  //drag scroll bar
  public dragScrollBarThumb(yMovement: number): void {
    const [yStart, yEnd] = this.camera.getYRange(); //camera coordinates
    const pixelHeight: number = yEnd - yStart;
    const pixelsToMove: number = yMovement * (this.maxHeight / pixelHeight);
    this.moveCameraByYPixels(pixelsToMove);
  }

  /** New buffer to render currently unassigned pairings onto a crew members row */
  public updateUnassignedPairings(unassignedCrewPairings: ICrewPairingMap): void {
    let didUpdate = false;
    let index = 0;
    this.unassignedCrewPairings = unassignedCrewPairings;
    for (const crewMember of this.crewList) {
      const crewId = isIPairingPosition(crewMember) ? `${crewMember.position}-${index}` : crewMember._id;
      const crewGL = this.crewGLMap[crewId];
      const unassignedCrewMemberPairings: ICrewPairing = unassignedCrewPairings[crewId];
      if (!crewGL || !unassignedCrewMemberPairings) {
        continue;
      }
      if (crewGL.pairingsGL.lastModified >= unassignedCrewMemberPairings.lastModified) {
        continue;
      }
      didUpdate = true;
      crewGL.pairingsGL.updateUnassignedPairingBuffers(this.gl, unassignedCrewMemberPairings.pairings);
      index++;
    }
    if (didUpdate) {
      this.lastModifiedTime = Date.now();
    }
  }

  public updatePairings(crewPairings: ICrewPairingMap): void {
    let didUpdate = false;
    let index = 0;
    this.crewPairings = crewPairings;
    for (const crewMember of this.crewList) {
      const crewId = isIPairingPosition(crewMember) ? `${crewMember.position}-${index}` : crewMember._id;
      const crewGL = this.crewGLMap[crewId];
      const crewMemberPairings: ICrewPairing = crewPairings[crewId];
      if (!crewGL || !crewMemberPairings) {
        continue;
      }

      if (crewGL.pairingsGL.lastModified >= crewMemberPairings.lastModified) {
        continue;
      }
      didUpdate = true;
      crewGL.pairingsGL.updatePairingBuffers(this.gl, crewMemberPairings.pairings);
      index++;
    }
    if (didUpdate) {
      this.lastModifiedTime = Date.now();
    }
  }

  /**
   * Function used to store vertex buffers for pref rank backgrounds.
   * @param crewId - crewId passed from parent canvas.
   * @returns
   */
  public updateCrewBidPrefRanks(crewId: string): void {
    if (!crewId || !this.bidRankSettings?.length || this.biddingBlockTimes?.length !== 2) {
      return;
    }
    const crewGL = this.crewGLMap[crewId];
    const start: number = this.biddingBlockTimes[0];
    const end: number = this.biddingBlockTimes[1];
    crewGL.pairingsGL.updateBidPrefRankBuffer(this.gl, this.bidRankSettings, start, end);
  }

  public updateCrewBidPairings(crewBidPairings: IBidResponse, awardedBidsVisible: boolean): void {
    let didUpdate = false;
    for (const [crewId, details] of Object.entries(crewBidPairings)) {
      let bidPairings: IDisplayBidPairing[] = [];
      let bidNFAs: IDisplayBidNFA[] = [];
      if (details?.bids) {
        Object.values(details?.bids).forEach((bid) => {
          // Spread crewmember bids and NFAs from all ranks into one single array.
          bidPairings = [...bidPairings, ...bid.displayBidPairings];
          bidNFAs = [...bidNFAs, ...bid.displayBidNFAs];
        });
      }
      // Bid Pairings
      const crewMemberBidPairings = {
        bidPairings: bidPairings
          ?.filter((bp) => {
            // If awarded and eligibleToAssign and hideAwardedBids all false, bid has been awarded to another crew member so hide (optionally).
            const hideBidPairing = !bp?.awarded && !bp?.eligibleToAssign && !awardedBidsVisible;
            return !hideBidPairing;
          })
          ?.sort((a, b) => a.start?.valueOf() - b.start?.valueOf()),
        lastModified: Date.now(),
      };
      this.crewBidPairings[crewId] = crewMemberBidPairings;
      // Bid NFAs
      const crewMemberBidNFAs = {
        bidNFAs: bidNFAs?.sort((a, b) => a.start?.valueOf() - b.start?.valueOf()),
        lastModified: Date.now(),
      };
      this.crewBidNFAs[crewId] = crewMemberBidNFAs;

      const crewGL = this.crewGLMap[crewId];
      // Update Bid Pairing buffers if required.
      if (crewGL?.pairingsGL && crewGL.pairingsGL.bidPairingslastModified < crewMemberBidPairings.lastModified) {
        didUpdate = true;
        crewGL.pairingsGL.updateBidPairingBuffers(this.gl, crewMemberBidPairings.bidPairings);
      }
      // Update Bid NFA buffers if required.
      if (
        crewGL?.nonFlyingAssignmentsGL &&
        crewGL.nonFlyingAssignmentsGL.bidNfaLastModified < crewMemberBidNFAs.lastModified
      ) {
        didUpdate = true;
        crewGL.nonFlyingAssignmentsGL.updateBidNfaBuffers(this.gl, crewMemberBidNFAs.bidNFAs);
      }
      if (didUpdate) {
        this.lastModifiedTime = Date.now();
      }
    }
  }

  public updateNFAs(crewNFAs: ICrewNonFlyingAssignmentMap): void {
    let didUpdate = false;
    let index = 0;
    this.crewNFAs = crewNFAs;
    for (const user of this.crewList) {
      const crewId = isIPairingPosition(user) ? `${user.position}-${index}` : user._id;
      const crewGL = this.crewGLMap[crewId];
      const crewMemberNFAs = crewNFAs[crewId];
      if (!crewGL || !crewMemberNFAs || crewGL.nonFlyingAssignmentsGL.lastModified >= crewMemberNFAs.lastModified) {
        continue;
      }
      didUpdate = true;
      crewGL.nonFlyingAssignmentsGL.updateBuffers(this.gl, crewMemberNFAs.nonFlyingAssignments);
      index++;
    }
    if (didUpdate) {
      this.lastModifiedTime = Date.now();
    }
  }

  public updateViolations(violations: ICrewViolationsMap): void {
    let index = 0;
    for (const user of this.crewList) {
      const crewId = isIPairingPosition(user) ? `${user.position}-${index}` : user._id;
      const crewGL = this.crewGLMap[crewId];
      const crewMemberViolations = !violations ? {} : violations?.[crewId]?.violations;
      if (!crewGL || !crewMemberViolations) {
        continue;
      }
      //only update the buffers is this a more recent violation calculation
      const createdAt = violations?.[crewId]?.createdAt ?? 0;
      if (crewGL?.violationsGL?.createdAt >= createdAt) {
        continue;
      }
      const violationBarHeight = baseRowHeight; //crewGL.rowHeight (always render the violation bar in the baseRowHeight)
      crewGL.violationsGL.updateViolationBuffers(this.gl, crewMemberViolations, violationBarHeight, createdAt);
      index++;
    }
  }

  /**
   * updateCrewQualifications updates the crewGL qualifications to draw up to date qual errors
   * crewQualifications - the map of qualification violations to draw
   */
  public updateCrewQualifications(crewQualifications: ICrewQualifications): void {
    for (const user of this.crewList) {
      if (isIPairingPosition(user)) {
        continue;
      }
      const crewId = user._id;
      const crewGL = this.crewGLMap[user._id];
      const crewMemberQualifications = crewQualifications[crewId];
      const currentQualificationsErrors = crewGL.qualificationsGL.getQualificationData();
      if (
        !crewGL ||
        (!crewMemberQualifications?.qualifications && !currentQualificationsErrors) //||
        // crewGL.qualificationsGL.lastModified >= crewMemberQualifications.lastModified
      ) {
        continue;
      }

      if (currentQualificationsErrors && !crewMemberQualifications?.qualifications) {
        //Should be clearing the errors from a crew who has no incoming errors but has old ones in the buffer
        crewGL.qualificationsGL.updateQualificationBuffers(this.gl, [] as IViolationResult[], crewGL.rowHeight, 0);
        continue;
      }

      if (!crewMemberQualifications) {
        console.warn("You are trying to set a crew's qualification error when they have no old or new errors");
        continue;
      }

      //only update the buffers is this a more recent violation calculation
      const createdAt = crewMemberQualifications.createdAt ?? 0;
      if (crewGL.qualificationsGL?.createdAt >= createdAt) {
        continue;
      }

      //keys are the requlation id
      const quals: IQualificationMap = crewMemberQualifications.qualifications;
      const keys = Object.keys(quals);
      const allQuals = [];
      for (const key of keys) {
        allQuals.push(...quals[key]);
      }
      crewGL.qualificationsGL.updateQualificationBuffers(
        this.gl,
        allQuals as IViolationResult[],
        crewGL.rowHeight,
        crewMemberQualifications.createdAt
      );
    }
  }

  //I think the long term plan is to calculate this with the violations on the backend so this well get removed.
  public updateCrewOverLaps(nfas: ICrewNonFlyingAssignmentMap, pairings: ICrewPairingMap): void {
    //This gets done whenever any crew's NFAs or Pairings change. Would be better if it was done on a per-crew change
    //but I am not sure where to watch that
    for (const user of this.crewList) {
      if (isIPairingPosition(user)) {
        continue;
      }
      const crewMemberOverLaps: IViolationResult[] = [];

      const crewId = user._id;
      const crewGL = this.crewGLMap[crewId];
      const crewNFAs = nfas[crewId]?.nonFlyingAssignments;
      const crewPairings = pairings[crewId]?.pairings;
      if (!crewNFAs) {
        continue;
      }
      const lastModified = Math.max(nfas[crewId]?.lastModified ?? 0, pairings[crewId]?.lastModified ?? 0);

      if (!crewGL || crewGL.overLapsGL.lastModified >= lastModified) {
        //if this crew's pairings or NFAs haven't changed since last time the buffer was updated
        //move on to the next crew
        continue;
      }

      for (const nfa of crewNFAs) {
        let overLapStart = null;
        let overLapEnd = null;

        const pairingIndexes = crewPairings
          ? getPairingsInRangeByRange(crewPairings, [nfa.start?.valueOf(), nfa.end?.valueOf()])
          : null;
        const nfaIndexes = crewNFAs
          ? getNFAsInRangeByRange(crewNFAs, [nfa.start?.valueOf(), nfa.end?.valueOf()])
          : null;

        for (let index = nfaIndexes?.minIndex; index <= nfaIndexes?.maxIndex; index++) {
          if (
            !!crewNFAs[index] &&
            !(
              crewNFAs[index]?.start?.valueOf() >= nfa.end?.valueOf() - minuteInMilliseconds ||
              nfa.start?.valueOf() + minuteInMilliseconds >= crewNFAs[index]?.end?.valueOf()
            ) &&
            nfa._id !== crewNFAs[index]?._id
          ) {
            overLapStart = Math.max(nfa.start?.valueOf(), crewNFAs[index]?.start?.valueOf());
            overLapEnd = Math.min(nfa.end?.valueOf(), crewNFAs[index]?.end?.valueOf());
            const overLap = {
              period: {
                start: overLapStart,
                end: overLapEnd,
              },
              regulation: `This nfa ${crewNFAs[index]?._id} - ${crewNFAs[index]?.assignmentType?.title} [${new Date(
                crewNFAs[index]?.start
              )}, ${new Date(crewNFAs[index]?.end)}] intersects `,
              title: "NFA over laps NFA",
              status: "fail", //this needs to be fail for it to show in violations buffer - don't want to change that atm 😉
            };
            crewMemberOverLaps.push(overLap);
          }
          // }
        }
        for (let index = pairingIndexes?.minIndex; index <= pairingIndexes?.maxIndex; index++) {
          if (
            !!crewPairings[index] &&
            !(crewPairings[index]?.start >= nfa.end || nfa.start >= crewPairings[index]?.end)
          ) {
            overLapStart = Math.max(nfa.start?.valueOf(), crewPairings[index]?.start?.valueOf());
            overLapEnd = Math.min(nfa.end?.valueOf(), crewPairings[index]?.end?.valueOf());
            const overLap = {
              period: {
                start: overLapStart,
                end: overLapEnd,
              },
              regulation: `This nfa ${crewNFAs[index]?._id} - ${crewNFAs[index]?.assignmentType?.title} [${new Date(
                crewNFAs[index]?.start
              )}, ${new Date(crewNFAs[index]?.end)}] intersects `,
              title: "NFA over laps Pairings",
              status: "fail", //this needs to be fail for it to show in violations buffer - don't want to change that atm ;)
            };
            crewMemberOverLaps.push(overLap);
          }
        }
      }

      if (!crewMemberOverLaps) {
        continue;
      }
      crewGL.overLapsGL.updateOverLapsBuffers(this.gl, crewMemberOverLaps, crewGL.rowHeight, lastModified);
    }
  }

  public updateCrewEvents(nfas: ICrewNonFlyingAssignmentMap, pairings: ICrewPairingMap): void {
    //This gets done whenever any crew's NFAs or Pairings change. Would be better if it was done on a per-crew change
    //but I am not sure where to watch that
    for (const user of this.crewList) {
      if (isIPairingPosition(user)) {
        continue;
      }
      // const crewMemberOverLaps: IViolationResult[] = [];

      const crewId = user._id;
      const crewEvents: ICrewScheduleEvent = this.crewEvents[crewId];
      const crewNFAs = nfas[crewId]?.nonFlyingAssignments;
      const crewPairings = pairings[crewId]?.pairings;
      if (!crewNFAs && !crewPairings) {
        continue;
      }
      const lastModified = Math.max(nfas[crewId]?.lastModified ?? 0, pairings[crewId]?.lastModified ?? 0);

      if (crewEvents?.lastModified >= lastModified) {
        //if this crew's pairings or NFAs haven't changed since last time the buffer was updated
        //move on to the next crew
        continue;
      }
      // const pairingIndexes = crewPairings ? getPairingsInRangeByRange(crewPairings, [nfa.start, nfa.end]) : null;
      // const nfaIndexes = crewNFAs ? getNFAsInRangeByRange(crewNFAs, [nfa.start, nfa.end]) : null;
      const baseNFAEvent: ICrewScheduleBaseEvent[] = crewNFAs
        ? crewNFAs.map((nfa) => ({
            _id: nfa._id,
            start: nfa.start?.valueOf(),
            end: nfa.end?.valueOf(),
            locationICAO: nfa.locationICAO,
            isDuty: nfa.assignmentType.isDuty,
            eventType: "nonflyingassignment",
          }))
        : [];
      const basePairingEvent: ICrewScheduleBaseEvent[] = crewPairings
        ? crewPairings.map((pairing) => ({
            _id: pairing._id,
            start: pairing.start?.valueOf(),
            end: pairing.end?.valueOf(),
            locationICAO: pairing.baseICAO,
            isDuty: true,
            eventType: "pairing",
          }))
        : [];

      const sortedBaseEvents = basePairingEvent
        .concat(baseNFAEvent)
        .sort((a: ICrewScheduleBaseEvent, b: ICrewScheduleBaseEvent) => a.start - b.start);
      this.crewEvents[crewId] = {
        events: sortedBaseEvents,
        lastModified: lastModified,
      };
    }
  }

  public updateDayLogicModifiers(crewDayLogicModifiers: ICrewLogicModifierMap): void {
    let index = 0;
    for (const user of this.crewList) {
      const crewId = isIPairingPosition(user) ? `${user.position}-${index}` : user._id;
      const crewGL = this.crewGLMap[crewId];

      const crewMemberDayLogicModifiers = crewDayLogicModifiers[crewId];
      if (
        !crewGL ||
        !crewMemberDayLogicModifiers //||
        // crewGL.violationsGL.lastModified >= crewMemberViolations.lastModified
      ) {
        continue;
      }
      crewGL.dayLogicModifiers = crewMemberDayLogicModifiers;
      index++;
    }
  }

  public drawPairingLogicModifiers(logicModifiers: IPairingsLogicModifierGL[], y: number): void {
    this.timelineLabelsGL.drawTimelinePairingLogicModifiers(this.camera, logicModifiers, y);
  }

  public updatePlannedEvents(crewPlannedEvents: ICrewSchedEventMap): void {
    let index = 0;
    for (const user of this.crewList) {
      const crewId = isIPairingPosition(user) ? `${user.position}-${index}` : user._id;
      const crewGL = this.crewGLMap[crewId];
      const crewMemberPlannedEvents = crewPlannedEvents[crewId];

      if (crewGL && crewMemberPlannedEvents) {
        if (crewGL.plannedEventsGL.lastModified >= crewMemberPlannedEvents?.lastModified) {
          continue;
        }
        crewGL.plannedEventsGL.updatePlannedEventsBuffers(
          this.gl,
          crewMemberPlannedEvents.plannedEvents,
          crewMemberPlannedEvents?.lastModified
        );
      }
      // crewGL.violationsGL.updateViolationsBuffers(this.gl, crewMemberPlannedEvents.violations);
      index++;
    }
  }

  private getRowFloorFromY(y: number): number[] {
    for (let i = this.minRowIndex; i <= this.maxRowIndex; i++) {
      const crew = this.crewList[i];
      const id = isIPairingPosition(crew) ? `${crew.position}-${i}` : crew._id;
      const crewGL = this.crewGLMap[id];
      if (!crewGL) {
        return [0, 0];
      }
      if (crewGL.rowStart <= y && y <= crewGL.rowStart + crewGL.rowHeight) {
        return [crewGL.rowStart, crewGL.rowHeight];
      }
    }
    return [0, 0];
  }

  public getHoveredEvents(): ICrewEvent[] {
    return this.hoveredEvents;
  }

  public setHoveredEvents(hoveredEvents: ICrewEvent[], yPixel: number): void {
    this.hoveredEvents = [...hoveredEvents];
    try {
      const eventOutlines = this.setOutlineEvents(hoveredEvents);
      this.outlinesGL.updateBuffers(this.gl, eventOutlines);
    } catch (e) {
      console.error(e);
    }
  }

  private setOutlineEvents(outlinedEvents: ICrewEvent[]): IOutlineBox[] {
    const mapToOutlines = (selectedEvent: ICrewEvent): IOutlineBox | null => {
      const { event, crewId } = selectedEvent;
      const crewGL = this.crewGLMap[crewId];
      if (!crewGL || (!crewGL.isExpanded && selectedEvent.isPlanned)) {
        return null;
      }
      // PAIRING / NFA
      const yStart = isIDisplayPairing(event) ? PairingsGL.pairingYStart : NonFlyingAssignmentsGL.yStart;
      const baseHeight = isIDisplayPairing(event) ? PairingsGL.pairingHeight : NonFlyingAssignmentsGL.height;
      const y0 = selectedEvent.isPlanned ? PlannedEventsGL.pairingYStart : yStart;
      const height = selectedEvent.isPlanned ? PlannedEventsGL.pairingHeight : baseHeight;
      return {
        x0: event.start?.valueOf() - Camera.referenceTime,
        x1: event.end?.valueOf() - Camera.referenceTime,
        y0: y0 + crewGL.rowStart,
        y1: y0 + crewGL.rowStart + height,
      };
    };
    // Create a set of outlines for all regular scheduled events.
    const scheduledEvents = outlinedEvents?.filter((crewEvent) => !crewEvent.event.hasOwnProperty("prefRank"));
    const scheduledEventOutlines = scheduledEvents
      ?.map((event) => mapToOutlines(event))
      ?.filter((val: IOutlineBox) => val);

    // Create a second set of outlines for all bid pairings events (Pairings and NFA events)
    const bidEvents = outlinedEvents?.filter((crewEvent) => crewEvent.event.hasOwnProperty("prefRank"));
    const prefRankBidPairingOutlines = (): any[] => {
      if (bidEvents?.length > 0) {
        // If interacting with Bid Pairings or NFAs, draw a second outline around assigned bid pairing.
        const bidOutlines = (selectedEvent: ICrewEvent): IOutlineBox | null => {
          const { event, crewId } = selectedEvent;
          const crewGL = this.crewGLMap[crewId];
          if (!crewGL || (!crewGL.isExpanded && selectedEvent.isPlanned)) {
            return null;
          }
          const bid = selectedEvent?.event as IDisplayBidNFA | IDisplayBidPairing;
          const stagingRowHeight = crewBidRowHeight + 4;
          const y0 =
            PairingsGL.pairingYStart + 28 + stagingRowHeight + bid.prefRank * crewBidRowHeight + bid.prefRank * 4;
          const height = crewBidRowHeight;
          return {
            x0: event.start?.valueOf() - Camera.referenceTime,
            x1: event.end?.valueOf() - Camera.referenceTime,
            y0: y0 + crewGL.rowStart,
            y1: y0 + crewGL.rowStart + height,
          };
        };
        return bidEvents?.map(bidOutlines)?.filter((val: IOutlineBox) => val);
      }
      return [];
    };

    const stagedBidEvents = outlinedEvents?.filter(
      (crewEvent) =>
        (crewEvent.event.hasOwnProperty("prefRank") &&
          (crewEvent?.event as IDisplayBidNFA | IDisplayBidPairing)?.assigned &&
          !(crewEvent?.event as IDisplayBidNFA | IDisplayBidPairing)?.awarded) ||
        (!(crewEvent?.event as IDisplayBidNFA | IDisplayBidPairing)?.assigned &&
          (crewEvent?.event as IDisplayBidNFA | IDisplayBidPairing)?.awarded)
    );
    const stagedBidOutlines = (): any[] => {
      if (bidEvents?.length > 0) {
        const mapToPrefRankOutlines = (selectedEvent: ICrewEvent): IOutlineBox | null => {
          const { event, crewId } = selectedEvent;
          const crewGL = this.crewGLMap[crewId];
          if (!crewGL || (!crewGL.isExpanded && selectedEvent.isPlanned)) {
            return null;
          }
          const stagingRowHeight = crewBidRowHeight + 4;
          const y0 = PairingsGL.pairingYStart + 28 + stagingRowHeight;
          const height = crewBidRowHeight;
          return {
            x0: event.start?.valueOf() - Camera.referenceTime,
            x1: event.end?.valueOf() - Camera.referenceTime,
            y0: y0 + crewGL.rowStart,
            y1: y0 + crewGL.rowStart + height,
          };
        };
        return stagedBidEvents?.map(mapToPrefRankOutlines)?.filter((val: IOutlineBox) => val);
      }
      return [];
    };

    // Return both normal outlines as well as bid pairing outlines (if applicable).
    return [...scheduledEventOutlines, ...prefRankBidPairingOutlines(), ...stagedBidOutlines()];
  }

  public redrawOutlines(): void {
    const eventOutlines = this.setOutlineEvents(this.outlinedEvents);
    this.selectedOutlinesGL.updateBuffers(this.gl, eventOutlines);
  }

  public setOutlinedEvents(outlinedEvents: ICrewEvent[]): void {
    this.outlinedEvents = [...outlinedEvents];
    const eventOutlines = this.setOutlineEvents(outlinedEvents);
    this.selectedOutlinesGL.updateBuffers(this.gl, eventOutlines);
  }

  public setFocusedEvents(focusedEvents: ICrewEvent[]): void {
    this.focusedEvents = [...focusedEvents];
    const eventOutlines = this.setOutlineEvents(focusedEvents);
    this.focusedOutlinesGL.updateBuffers(this.gl, eventOutlines);
  }

  private setOutlineDay(selectedCrewDay: ISelectedCrewDay): IOutlineBox | null {
    const { selectedTimes, crewId } = selectedCrewDay;
    const crewGL = this.crewGLMap[crewId];
    if (!crewGL || !selectedCrewDay) {
      return null;
    }
    const yStart = NonFlyingAssignmentsGL.yStart;
    const height = NonFlyingAssignmentsGL.height;

    const startTime = this.baseTime.isUTC
      ? moment(selectedTimes).utc()
      : moment(selectedTimes).tz(this.baseTime.timezoneId, true);
    const endTime = this.baseTime.isUTC
      ? moment(selectedTimes).utc()
      : moment(selectedTimes).tz(this.baseTime.timezoneId, true);
    return {
      x0: startTime.startOf("day")?.valueOf() - Camera.referenceTime,
      x1: endTime.endOf("day")?.valueOf() - Camera.referenceTime,
      y0: crewGL.rowStart + yStart,
      y1: crewGL.rowStart + yStart + height,
    };
  }

  private getOutlineDays(crewGL: ICrewGL, outlineTimes: [moment.Moment, moment.Moment]): IOutlineBox {
    const yStart = CrewScheduleErrorsGL.yHeight;
    const height = crewGL.isExpanded ? expandedRowHeight : baseRowHeight;

    const [start, end] = outlineTimes;
    const startTime = this.baseTime.isUTC ? moment(start).utc() : moment(start).tz(this.baseTime.timezoneId, true);
    const endTime = this.baseTime.isUTC ? moment(end).utc() : moment(end).tz(this.baseTime.timezoneId, true);
    return {
      x0: startTime?.valueOf() - Camera.referenceTime,
      x1: endTime?.valueOf() - Camera.referenceTime,
      y0: crewGL.rowStart + yStart,
      y1: crewGL.rowStart + yStart + height - CrewScheduleErrorsGL.yHeight,
    };
  }

  public setSelectedCrewDays(selectedCrewDays: ISelectedCrewDays): void {
    this.selectedCrewDays = selectedCrewDays;
    if (!selectedCrewDays) {
      this.selectedDayOutlinesGL.updateBuffers(this.gl, []);
    } else {
      const { crewId, selectedTimes } = selectedCrewDays;

      const crewGL = this.crewGLMap[crewId];
      if (!crewGL || !crewId || selectedTimes.length === 0) {
        return;
      }
      const times = [...selectedTimes];
      times.sort();
      let currentTime = moment
        .utc(times.shift())
        .tz(this.baseTime?.timezoneId ?? "Etc/UTC")
        .endOf("day");
      let firstTime = moment
        .utc(currentTime)
        .tz(this.baseTime?.timezoneId ?? "Etc/UTC")
        .startOf("day");
      const eventOutlines: IOutlineBox[] = [];

      for (const time of times) {
        const nextTime = moment
          .utc(time)
          .tz(this.baseTime?.timezoneId ?? "Etc/UTC")
          .endOf("day");
        if (nextTime.diff(currentTime, "days") > 1) {
          eventOutlines.push(this.getOutlineDays(crewGL, [firstTime, currentTime]));
          firstTime = moment
            .utc(nextTime)
            .tz(this.baseTime?.timezoneId ?? "Etc/UTC")
            .startOf("day");
        }
        currentTime = nextTime;
      }
      eventOutlines.push(this.getOutlineDays(crewGL, [firstTime, currentTime]));
      if (eventOutlines.length > 0) {
        this.selectedDayOutlinesGL.updateBuffers(this.gl, eventOutlines);
      } else {
        this.selectedDayOutlinesGL.updateBuffers(this.gl, []);
      }
    }
    this.updateMinMaxRows();
  }

  public getSelectedCrewDays(): ISelectedCrewDays {
    return this.selectedCrewDays;
  }

  public getSelectedCrewDayDeltaRow(direction: -1 | 1, updateCamera = false): string {
    const currentCrewId = this.selectedCrewDays?.crewId;

    const crewIndex = this.crewList.findIndex((value: ICrewPairingPosition) => {
      const id = isIPairingPosition(value) ? value.crew_id : value._id;
      return id === currentCrewId;
    });
    const newIndex = direction === 1 ? Math.min(crewIndex + 1, this.crewList.length - 1) : Math.max(crewIndex - 1, 0);
    const newCrewMember = this.crewList[newIndex];

    const newCrewMemberId = isIPairingPosition(newCrewMember) ? newCrewMember.crew_id : newCrewMember._id;
    if (updateCamera && this.selectedCrewDays) {
      const oldCrewGL = this.crewGLMap[currentCrewId].rowStart;
      const crewGL = this.crewGLMap[newCrewMemberId].rowStart;
      this.moveCameraByYPixels(direction * Math.abs(crewGL - oldCrewGL));
    }
    return newCrewMemberId;
  }

  // Check with Rhys how the new selectedDays array affects this function and whether it needs
  // to be modified.
  public getSelectedCrewDayDeltaTime(direction: -1 | 1, updateCamera = false): number {
    const currentTime = this.selectedCrewDays?.selectedTimes[0];
    const timeMoment = this.baseTime.isUTC
      ? moment(currentTime).utc()
      : moment(currentTime).tz(this.baseTime.timezoneId);
    const newTime = timeMoment.add(direction, "day")?.valueOf();
    if (updateCamera && currentTime) {
      this.camera.panX(direction * Math.abs(newTime - currentTime));
    }
    return newTime;
  }

  // Override
  public zoomCamera = (deltaZoom: number): void => {
    super.zoomCamera(deltaZoom);
    const zoomTime = this.pixelCoordsToTimeAxis(deltaZoom);

    this.camera.zoom(zoomTime);
    this.updateDayScale();
  };

  private updateDayScale(): void {
    const [minTime, maxTime] = this.camera.getXRange();

    this.dayScale = oneDayMillis;
    this.numDaysInView = (maxTime - minTime) / oneDayMillis + 1;
  }

  // public updateCrewPairing(pairing: IDisplayPairing): void {}

  public updateMinMaxRows(): void {
    const [minY, maxY] = this.camera.getYRange();
    let foundMin = false;
    let foundMax = false;
    let index = 0;

    for (const crewMember of this.crewList) {
      const crewId = isIPairingPosition(crewMember) ? `${crewMember.position}-${index}` : crewMember._id;

      const crewGL = this.crewGLMap[crewId];
      if (!crewGL) {
        return;
      }
      if (!foundMin && crewGL.rowStart + crewGL.rowHeight > minY) {
        this.minRowIndex = index;
        foundMin = true;
      }

      if (!foundMax && crewGL.rowStart + crewGL.rowHeight > maxY) {
        this.maxRowIndex = index;
        foundMax = true;
      }

      if (foundMin && foundMax) {
        return;
      }
      index++;
    }

    this.minRowIndex = foundMin ? this.minRowIndex : 0;
    this.maxRowIndex = foundMax ? this.maxRowIndex : this.crewList.length - 1;
  }

  public setViewport(width: number, height: number): void {
    super.setViewport(width, height);
    this.updateMinMaxRows();
    this.updateDayScale();
    this.recalculateScrollBar();
  }

  public moveCameraByYPixels(pixels: number): void {
    super.moveCameraByYPixels(pixels);
    this.updateMinMaxRows();
  }

  /**
   * setHideDropOffHours setter to display drop off hours or not
   * NOTE: Will always show warning drop off hours that are less than 20% available hours left
   * @param hideDropOffHours - whether or not to hide drop off hours
   */
  public setHideDropOffHours(hideDropOffHours: boolean): void {
    this.hideDropOffHours = hideDropOffHours;
  }

  /**
   * To toggle the hideTimeBetweenEvents between event labels from being drawn
   * @param hideTimeBetweenEvents - whether or not to hid
   */
  public setHideTimeBetweenEvents(hideTimeBetweenEvents: boolean): void {
    this.hideTimeBetweenEvents = hideTimeBetweenEvents;
  }

  /**
   * To toggle the check-in/check-out time labels from being drawn
   * @param hideCheckInOutTimes - whether or not to hid
   */
  public setHideCheckInOutTimes(hideCheckInOutTimes: boolean): void {
    this.hideCheckInOutTimes = hideCheckInOutTimes;
  }

  public draw(): void {
    super.draw();

    if (this.crewList.length === 0) {
      return;
    }

    this.colPainter.draw(this.gl, this.camera, this.dayScale, this.numDaysInView, this.theme.oddRow, this.baseTime);

    const white: IRGBA = {
      red: 1.0,
      green: 1.0,
      blue: 1.0,
      alpha: 0.2,
    };

    const biddingStartTimeInPixels: number = this.biddingBlockTimes?.[0]
      ? this.camera.timeAxisToPixelCoords(this.biddingBlockTimes[0], this.canvas.width)
      : 0;
    const biddingEndTimeInPixels: number = this.biddingBlockTimes?.[1]
      ? this.camera.timeAxisToPixelCoords(this.biddingBlockTimes[1], this.canvas.width)
      : 0;
    // If bidding mode is active, draw bar and indicators in time axis for start/end of bidding period.
    if (this.bidAssignmentMode && this.biddingBlockTimes?.length === 2) {
      this.timelineLabelsGL.drawColoredRectangle(
        {
          color: this.theme.biddingActiveButtonColor,
          start: biddingStartTimeInPixels,
          end: biddingEndTimeInPixels,
          height: CrewScheduleGL.biddingWindowBarHeight,
        },
        -10,
        null,
        this.theme.biddingActiveButtonColor // optional parameter which renders indicator bars if present
      );
    }

    for (let i = this.minRowIndex; i <= this.maxRowIndex; i++) {
      const crewMember = this.crewList[i];
      const crewId = isIPairingPosition(crewMember) ? `${crewMember.position}-${i}` : crewMember._id;
      const crewGL = this.crewGLMap[crewId];

      // Crew row divider bar
      crewGL.violationBar.draw(this.gl, this.camera, i, white, 6, crewGL.rowStart);

      if (!isIPairingPosition(crewMember)) {
        crewGL.pairingsGL.draw(this.gl, this.camera, crewGL.rowStart);
        crewGL.nonFlyingAssignmentsGL.draw(this.gl, this.camera, crewGL.rowStart);

        crewGL.overLapsGL.drawTopStrip?.(this.gl, this.camera, crewGL.rowStart);
        crewGL.qualificationsGL.drawTopStrip?.(this.gl, this.camera, crewGL.rowStart);
        crewGL.violationsGL.drawTopStrip?.(this.gl, this.camera, crewGL.rowStart);

        crewGL.violationsGL.drawRunningTotals(
          this.timelineLabelsGL.getContext(),
          this.camera,
          this.canvas.width,
          crewGL.rowStart + crewGL.rowHeight,
          this.hideDropOffHours
        );

        // PER-DAY VERTICAL OVERLAYS (gradient/red-yellow-orange)
        const stripBottom = CrewScheduleErrorsGL.yStart + CrewScheduleErrorsGL.yHeight;
        const yBandTop = crewGL.rowStart + stripBottom + 1;
        const yBandBottom = crewGL.rowStart + crewGL.rowHeight - 2;

        // loop visible days
        const [minX, maxX] = this.camera.getXRange();
        let dayStart = moment.utc(minX).startOf("day").valueOf();
        const lastDay = moment.utc(maxX).endOf("day").valueOf();

        const stripJobs: Array<{ start: number; end: number; mode: DayMode }> = [];

        while (dayStart <= lastDay) {
          const dayEnd = moment.utc(dayStart).endOf("day").valueOf();

          // Build mode bitmask for this day (1=red, 2=yellow, 4=orange)
          const hasR = crewGL.violationsGL.hasOnDay(dayStart, dayEnd);
          const hasY = crewGL.qualificationsGL.hasOnDay(dayStart, dayEnd);
          const hasO = crewGL.overLapsGL.hasOnDay(dayStart, dayEnd);

          const mode = makeDayMode(hasR, hasY, hasO);
          const bits = (mode & 1 ? 1 : 0) + (mode & 2 ? 1 : 0) + (mode & 4 ? 1 : 0);

          if (mode !== 0) {
            // Draw the vertical band for this day (PerDayViolationOverlayGL handles blending)
            // z slightly behind NFAs/pairings
            this.perDayOverlayGL.drawDay(
              this.gl,
              this.camera,
              yBandTop,
              yBandBottom,
              dayStart,
              dayEnd,
              -5.2, // behind NFAs & pairings
              mode,
              0.6, // overlay alpha
              0.95, // tint
              0.6
            );
          }

          if (bits >= 2) {
            stripJobs.push({ start: dayStart, end: dayEnd, mode });
          }

          dayStart = moment.utc(dayStart).add(1, "day").valueOf();
        }

        if (stripJobs.length) {
          const yStripTop = crewGL.rowStart + CrewScheduleErrorsGL.yStart;
          const stripH = CrewScheduleErrorsGL.yHeight;
          for (const d of stripJobs) {
            this.perDayOverlayGL.drawTopStripCombined(this.gl, this.camera, yStripTop, stripH, d.start, d.end, d.mode);
          }
        }

        const drawBiddingPrefRanks = this.bidAssignmentMode && !!this.bidRankSettings?.length && crewGL?.isExpanded;
        if (this.bidAssignmentMode && drawBiddingPrefRanks) {
          // Draw bid rank background rows.
          crewGL.pairingsGL.drawBidRankBackgrounds(this.gl, this.camera, crewGL.rowStart);
          // Draw bid Pairings/labels
          if (this.crewBidPairings[crewId]?.bidPairings?.length) {
            crewGL.pairingsGL.drawBidPairings(this.gl, this.camera, crewGL.rowStart);
            this.drawPairingLabels(this.camera, this.crewBidPairings[crewId].bidPairings, crewGL.rowStart);
          }
          // Draw Bid NFAs/labels
          if (this.crewBidNFAs[crewId]?.bidNFAs?.length) {
            crewGL.nonFlyingAssignmentsGL.drawBidNFAs(this.gl, this.camera, crewGL.rowStart);
            this.drawNFALabels(this.camera, this.crewBidNFAs[crewId].bidNFAs, crewGL.rowStart);
          }
          // Draw assignment/unassignment arrows if applicable for hovered events
          if (this.hoveredEvents?.length > 0) {
            const [yMin] = this.camera.getYRange();
            drawBidAssignmentIndicators(
              this.timelineLabelsGL.getContext(),
              this.camera,
              crewGL,
              this.hoveredEvents,
              crewGL.rowStart - yMin,
              this.timelineLabelsGL.getBiddingNFARowHeight()
            );
          }
        }

        if (crewGL.dayLogicModifiers && Object.keys(crewGL.dayLogicModifiers).length > 0) {
          this.drawDayLogicModifiers(this.camera, crewGL.dayLogicModifiers, crewGL.rowStart);
        }

        // Only draw planned events if NOT in bid assignment mode
        if (crewGL.isExpanded && !this.bidAssignmentMode) {
          crewGL.plannedEventsGL.draw(this.gl, this.camera, crewGL.rowStart);
          //draw labels
          // crewGL.plannedEventsGL.plannedPairings &&
          //   this.drawPairingLabels(this.camera, crewGL.plannedEventsGL.plannedPairings, crewGL.rowStart + 40);
          // crewGL.plannedEventsGL.plannedNFAs &&
          //   this.drawNFALabels(this.camera, crewGL.plannedEventsGL.plannedNFAs, crewGL.rowStart);
        }
        if (!!this.crewNFAs && !!this.crewNFAs[crewId]) {
          this.drawNFALabels(this.camera, this.crewNFAs[crewId].nonFlyingAssignments, crewGL.rowStart);
        }

        if (!!this.crewPairings && !!this.crewPairings[crewId]) {
          const modifiers = crewGL.pairingsGL?.getLogicModifiers();
          this.drawPairingLogicModifiers(modifiers, crewGL.rowStart + PairingsGL.pairingYStart);
          this.drawPairingLabels(
            this.camera,
            this.crewPairings[crewId].pairings,
            crewGL.rowStart,
            !this.hideCheckInOutTimes
          );

          const checkInOverrides = crewGL.pairingsGL?.getcheckInOuts();
          drawCheckInOutIndicators(
            this.timelineLabelsGL.getContext(),
            this.camera,
            crewGL,
            checkInOverrides,
            this.returnNFARowHeight()
          );
        }

        // Draw any unassigned pairings (i.e open flying requests) for this crew member
        if (this.unassignedCrewPairings?.[crewId]?.pairings) {
          const unassignedPairing = this.unassignedCrewPairings[crewId].pairings[0];
          const startTimeInPixels: number = unassignedPairing.start
            ? this.camera.timeAxisToPixelCoords(unassignedPairing.start as number, this.canvas.width)
            : 0;
          const endTimeInPixels: number = unassignedPairing.end
            ? this.camera.timeAxisToPixelCoords(unassignedPairing.end as number, this.canvas.width)
            : 0;
          this.timelineLabelsGL.drawColoredRectangle(
            {
              color: "#224f6d", // this color should match the selected row color.
              start: startTimeInPixels,
              end: endTimeInPixels,
              height: CrewScheduleGL.biddingWindowBarHeight,
            },
            -10,
            null
          );
          crewGL.pairingsGL.drawUnassignedPairings(this.gl, this.camera, crewGL.rowStart);
          this.drawPairingLabels(this.camera, this.unassignedCrewPairings[crewId].pairings, crewGL.rowStart);
        }

        if (
          !this.hideTimeBetweenEvents &&
          !!this.crewEvents &&
          !!this.crewEvents[crewId] &&
          this.crewEvents[crewId].events?.length > 0
        ) {
          this.drawRestTime(this.camera, this.crewEvents?.[crewId].events, crewGL.rowStart);
        }

        // Draw ghosted pairing if user is hovering over the crew sched with a desired pairing assignment.
        if (this.ghostPairing?.crewId === crewMember._id) {
          const crewPairings = this.crewPairings?.[crewMember._id]?.pairings ?? [];
          const crewNFAs = this.crewNFAs?.[crewId]?.nonFlyingAssignments ?? [];
          this.drawGhostPairing(crewGL, crewPairings, crewNFAs, this.ghostPairing, crewMember);
        }
      }
    }

    this.outlinesGL.draw(this.gl, this.camera, this.theme.hoverFlightColor, -1.4);
    this.selectedOutlinesGL.draw(this.gl, this.camera, this.theme.selectedFlightColor, -1.1);
    this.selectedDayOutlinesGL.draw(this.gl, this.camera, this.theme.selectedFlightColor, -5, true);
    this.focusedOutlinesGL.draw(this.gl, this.camera, this.focusedEventOutlineColor, -1.0);
    this.scrollBarWrapper.drawBar(
      this.gl,
      this.camera,
      this.canvas,
      this.theme.scrollBarBackGround,
      this.scrollBarThumbColor ?? this.theme.scrollBarThumb,
      this.maxHeight
    );
  }
}
